import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { CheckCircle2, Loader2, AlertTriangle, ArrowRight, Calendar } from "lucide-react";

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshAuth } = useAuth();
  const [status, setStatus] = useState(() => {
    // Initialize status based on sessionId presence
    const sid = searchParams.get("session_id");
    return sid ? "loading" : "error";
  });
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const maxPollAttempts = 5;

  const sessionId = searchParams.get("session_id");

  const pollPaymentStatus = useCallback(async () => {
    if (!sessionId) return true;
    
    try {
      const response = await api.get(`/checkout/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === "paid") {
        setPaymentInfo(data);
        setStatus("success");
        // Refresh auth to get updated subscription info
        await refreshAuth();
        return true;
      } else if (data.status === "expired") {
        setStatus("error");
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error checking payment status:", error);
      return false;
    }
  }, [sessionId, refreshAuth]);

  useEffect(() => {
    if (!sessionId) return;

    const poll = async () => {
      const done = await pollPaymentStatus();
      if (!done && pollCount < maxPollAttempts) {
        // Schedule next poll
        const timer = setTimeout(() => {
          setPollCount(prev => prev + 1);
        }, 2000);
        return () => clearTimeout(timer);
      } else if (!done && pollCount >= maxPollAttempts) {
        setStatus("error");
      }
    };

    poll();
  }, [sessionId, pollCount, pollPaymentStatus]);

  const handleRetry = () => {
    setStatus("loading");
    setPollCount(0);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {status === "loading" && (
            <>
              <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mx-auto mb-4">
                <Loader2 className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
              </div>
              <CardTitle>Processing Payment...</CardTitle>
              <CardDescription>
                Please wait while we confirm your payment
              </CardDescription>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
              </div>
              <CardTitle>Payment Successful!</CardTitle>
              <CardDescription>
                Thank you for your purchase
              </CardDescription>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <CardTitle>Payment Issue</CardTitle>
              <CardDescription>
                There was an issue confirming your payment
              </CardDescription>
            </>
          )}
        </CardHeader>

        <CardContent className="space-y-6">
          {status === "success" && paymentInfo && (
            <div className="bg-muted/50 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Amount</span>
                <span className="font-semibold">
                  £{paymentInfo.amount_total?.toFixed(2)}
                </span>
              </div>
              {paymentInfo.subscription_tier && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Plan</span>
                  <span className="font-semibold capitalize">
                    {paymentInfo.subscription_tier}
                  </span>
                </div>
              )}
              {paymentInfo.additional_employees && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Additional Staff</span>
                  <span className="font-semibold">
                    +{paymentInfo.additional_employees} employees
                  </span>
                </div>
              )}
            </div>
          )}

          {status === "loading" && (
            <div className="text-center text-sm text-muted-foreground">
              <p>Verifying payment status...</p>
              <p className="mt-2">Attempt {pollCount + 1} of {maxPollAttempts}</p>
            </div>
          )}

          {status === "error" && (
            <div className="text-center text-sm text-muted-foreground">
              <p>We could not confirm your payment at this time.</p>
              <p className="mt-2">
                If you were charged, your subscription will be activated shortly.
                Please contact support if the issue persists.
              </p>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <Button
              className="w-full"
              onClick={() => navigate("/dashboard")}
              data-testid="go-to-dashboard-btn"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Go to Dashboard
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>

            {status === "error" && (
              <Button
                variant="outline"
                className="w-full"
                onClick={handleRetry}
              >
                Try Again
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
